import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fetchResources, fetchProductionManagement, fetchAllTimesheets } from '@/lib/notion-client';

interface Resource {
  id: string;
  name: string;
  email: string;
  role?: string[];
  department?: string[];
  weeklyCapacity?: number | string;
  hourlyRate?: number | string;
  active?: boolean;
  activeResource?: boolean;
}

interface NotionUser {
  id: string;
  name: string;
  email: string;
  profileImage?: string;
}

interface Project {
  id: string;
  name: string;
  status?: string;
  tags?: string[];
  startDate?: string;
  endDate?: string;
  budget?: number;
  spent?: number;
  progress?: number;
  owner?: string[];  // Array of Notion user IDs
  collaborators?: string[];  // Array of Notion user IDs
  [key: string]: any;
}

interface ProductionTimesheet {
  id: string;
  taskId?: string;
  taskTitle?: string;
  contributors: string[]; // Array of resource/user IDs
  projectId?: string;
  projectName?: string;
  startTime?: string;
  endTime?: string;
  manualMinutes?: number | null;
  duration?: number; // calculated in minutes
}

interface RealizedBudgetBreakdown {
  totalRealized: number;
  totalHours: number;
  byTask: {
    taskId: string;
    taskTitle: string;
    hours: number;
    cost: number;
    timesheets: {
      id: string;
      contributorId: string;
      contributorName: string;
      hours: number;
      hourlyRate: number;
      cost: number;
      date: string;
    }[];
  }[];
  byResource: {
    resourceId: string;
    resourceName: string;
    hours: number;
    hourlyRate: number;
    cost: number;
  }[];
}

interface ProductionManagementContextType {
  resources: Resource[];
  projects: Project[];
  users: NotionUser[];
  timesheets: ProductionTimesheet[];
  loadingResources: boolean;
  loadingProjects: boolean;
  loadingUsers: boolean;
  loadingTimesheets: boolean;
  errorResources: string | null;
  errorProjects: string | null;
  errorUsers: string | null;
  errorTimesheets: string | null;
  refreshResources: (force?: boolean) => Promise<void>;
  refreshProjects: (force?: boolean) => Promise<void>;
  refreshUsers: () => Promise<void>;
  refreshTimesheets: (force?: boolean) => Promise<void>;
  refreshAll: (force?: boolean) => Promise<void>;
  getUserById: (id: string) => NotionUser | undefined;
  getUsersByIds: (ids?: string[]) => NotionUser[];
  getUsersByEmails: (emails?: string[]) => NotionUser[];
  getTimesheetsByContributorId: (contributorId: string) => ProductionTimesheet[];
  getTimesheetsByTaskIds: (taskIds: string[]) => ProductionTimesheet[];
  getResourceHourlyRate: (resourceId: string) => number;
  calculateRealizedBudget: (taskIds: string[], taskTitleMap?: Map<string, string>) => RealizedBudgetBreakdown;
}

const ProductionManagementContext = createContext<ProductionManagementContextType | undefined>(undefined);

export function ProductionManagementProvider({ children }: { children: React.ReactNode }) {
  const [resources, setResources] = useState<Resource[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<NotionUser[]>([]);
  const [timesheets, setTimesheets] = useState<ProductionTimesheet[]>([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);
  const [errorResources, setErrorResources] = useState<string | null>(null);
  const [errorProjects, setErrorProjects] = useState<string | null>(null);
  const [errorUsers, setErrorUsers] = useState<string | null>(null);
  const [errorTimesheets, setErrorTimesheets] = useState<string | null>(null);

  const refreshResources = useCallback(async (force = false) => {
    try {
      setLoadingResources(true);
      setErrorResources(null);
      
      const json = await fetchResources(force);
      
      // Map notionId to id for each resource
      const resourcesData = (json.resources || []).map((r: any) => ({
        ...r,
        id: r.notionId || r.id,
        active: r.activeResource !== undefined ? r.activeResource : true,
      }));
      
      setResources(resourcesData);
    } catch (err) {
      setErrorResources(err instanceof Error ? err.message : 'Failed to load resources');
      console.error('Failed to fetch resources:', err);
    } finally {
      setLoadingResources(false);
    }
  }, []);

  const refreshProjects = useCallback(async (force = false) => {
    try {
      setLoadingProjects(true);
      setErrorProjects(null);
      
      const json = await fetchProductionManagement(force);
      
      const projectsData = json.projects || [];
      setProjects(projectsData);
    } catch (err) {
      setErrorProjects(err instanceof Error ? err.message : 'Failed to load projects');
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      setErrorUsers(null);
      
      const config = (window as any).WP_COCKPIT;
      if (!config) throw new Error('WP Cockpit config not available');
      
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/projects/management/users`;
      const headers: Record<string, string> = {};
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce;
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers
      });

      const data = await response.json();

      if (response.ok && Array.isArray(data)) {
        setUsers(data);
      } else {
        throw new Error('Failed to load users');
      }
    } catch (err) {
      setErrorUsers(err instanceof Error ? err.message : 'Failed to load users');
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const refreshTimesheets = useCallback(async (force = false) => {
    try {
      setLoadingTimesheets(true);
      setErrorTimesheets(null);
      
      const wpConfig = (window as any).WP_COCKPIT;
      const isDev = wpConfig?.devMode || false;
      
      if (isDev) {
        console.log('[ProductionContext] Fetching timesheets, force:', force);
      }
      
      const json = await fetchAllTimesheets(force);
      
      // Map the response to our interface - adapt field names as needed based on webhook response
      const timesheetsData: ProductionTimesheet[] = (json.timesheets || json.data?.timesheets || []).map((t: any) => {
        // Calculate duration from startTime/endTime if not provided
        let duration = t.duration || t.durationMinutes || t.minutes;
        if (!duration && t.startTime && t.endTime) {
          const start = new Date(t.startTime);
          const end = new Date(t.endTime);
          duration = Math.round((end.getTime() - start.getTime()) / 60000);
        }
        
        return {
          id: t.id || t.notionId,
          taskId: t.taskId || t.task_id,
          taskTitle: t.taskTitle || t.task_title || t.taskName,
          contributors: Array.isArray(t.contributors) ? t.contributors.map(String) : [],
          projectId: t.projectId || t.project_id,
          projectName: t.projectName || t.project_name,
          startTime: t.startTime || t.start_time || t.startDate,
          endTime: t.endTime || t.end_time || t.endDate,
          manualMinutes: t.manualMinutes ?? null,
          duration,
        };
      });
      
      setTimesheets(timesheetsData);
      
      if (isDev) {
        console.log('[ProductionContext] Loaded timesheets:', timesheetsData.length);
      }
    } catch (err) {
      setErrorTimesheets(err instanceof Error ? err.message : 'Failed to load timesheets');
      console.error('Failed to fetch timesheets:', err);
    } finally {
      setLoadingTimesheets(false);
    }
  }, []);

  const refreshAll = useCallback(async (force = false) => {
    await Promise.all([
      refreshResources(force),
      refreshProjects(force),
      refreshUsers(),
      refreshTimesheets(force)
    ]);
  }, [refreshResources, refreshProjects, refreshUsers, refreshTimesheets]);

  // Initial load
  useEffect(() => {
    refreshAll();
  }, []);

  // Helper functions to resolve users from IDs
  const getUserById = useCallback((id: string): NotionUser | undefined => {
    return users.find(user => user.id === id);
  }, [users]);

  const getUsersByIds = useCallback((ids?: string[]): NotionUser[] => {
    if (!ids || ids.length === 0) return [];
    return ids
      .map(id => users.find(user => user.id === id))
      .filter((user): user is NotionUser => user !== undefined);
  }, [users]);

  const getUsersByEmails = useCallback((emails?: string[]): NotionUser[] => {
    const wpConfig = (window as any).WP_COCKPIT
    const isDev = wpConfig?.devMode || false
    
    if (isDev) {
      console.log('[ProductionContext] getUsersByEmails called')
      console.log('[ProductionContext] Looking for emails:', emails)
      console.log('[ProductionContext] Available users count:', users.length)
      console.log('[ProductionContext] Available users:', users.map(u => ({ id: u.id, name: u.name, email: u.email })))
    }
    
    if (!emails || emails.length === 0) {
      if (isDev) console.log('[ProductionContext] No emails provided, returning empty array')
      return [];
    }
    
    const matched = emails
      .map(email => {
        const found = users.find(user => user.email === email)
        if (isDev) {
          console.log(`[ProductionContext] Email "${email}" matched:`, found ? found.name : 'NO MATCH')
        }
        return found
      })
      .filter((user): user is NotionUser => user !== undefined);
    
    if (isDev) {
      console.log('[ProductionContext] Final matched users:', matched)
    }
    
    return matched;
  }, [users]);

  // Helper function to filter timesheets by contributor ID
  const getTimesheetsByContributorId = useCallback((contributorId: string): ProductionTimesheet[] => {
    if (!contributorId) return [];
    return timesheets.filter(t => t.contributors.includes(contributorId));
  }, [timesheets]);

  // Helper function to filter timesheets by task IDs
  const getTimesheetsByTaskIds = useCallback((taskIds: string[]): ProductionTimesheet[] => {
    if (!taskIds || taskIds.length === 0) return [];
    return timesheets.filter(t => t.taskId && taskIds.includes(t.taskId));
  }, [timesheets]);

  // Helper function to get hourly rate for a resource
  const getResourceHourlyRate = useCallback((resourceId: string): number => {
    const resource = resources.find(r => r.id === resourceId);
    if (!resource?.hourlyRate) return 0;
    return typeof resource.hourlyRate === 'number' 
      ? resource.hourlyRate 
      : parseFloat(resource.hourlyRate) || 0;
  }, [resources]);

  // Calculate realized budget for a set of task IDs
  const calculateRealizedBudget = useCallback((taskIds: string[], taskTitleMap?: Map<string, string>): RealizedBudgetBreakdown => {
    const projectTimesheets = getTimesheetsByTaskIds(taskIds);
    
    const byTaskMap: Record<string, { 
      taskTitle: string; 
      hours: number; 
      cost: number; 
      timesheets: any[] 
    }> = {};
    
    const byResourceMap: Record<string, { 
      resourceName: string; 
      hours: number; 
      hourlyRate: number; 
      cost: number 
    }> = {};
    
    let totalRealized = 0;
    let totalHours = 0;
    
    projectTimesheets.forEach(ts => {
      const hours = (ts.duration || 0) / 60;
      const taskId = ts.taskId || 'unknown';
      const taskTitle = taskTitleMap?.get(taskId) || ts.taskTitle || 'Untitled Task';
      
      // Initialize task entry
      if (!byTaskMap[taskId]) {
        byTaskMap[taskId] = { taskTitle, hours: 0, cost: 0, timesheets: [] };
      }
      
      // Process each contributor
      ts.contributors.forEach(contributorId => {
        const hourlyRate = getResourceHourlyRate(contributorId);
        const cost = hours * hourlyRate;
        const resource = resources.find(r => r.id === contributorId);
        const contributorName = resource?.name || 'Unknown';
        
        totalRealized += cost;
        totalHours += hours;
        
        // Add to task breakdown
        byTaskMap[taskId].hours += hours;
        byTaskMap[taskId].cost += cost;
        byTaskMap[taskId].timesheets.push({
          id: ts.id,
          contributorId,
          contributorName,
          hours,
          hourlyRate,
          cost,
          date: ts.startTime || '',
        });
        
        // Add to resource breakdown
        if (!byResourceMap[contributorId]) {
          byResourceMap[contributorId] = { 
            resourceName: contributorName, 
            hours: 0, 
            hourlyRate, 
            cost: 0 
          };
        }
        byResourceMap[contributorId].hours += hours;
        byResourceMap[contributorId].cost += cost;
      });
    });
    
    return {
      totalRealized,
      totalHours,
      byTask: Object.entries(byTaskMap).map(([taskId, data]) => ({
        taskId,
        ...data,
      })),
      byResource: Object.entries(byResourceMap).map(([resourceId, data]) => ({
        resourceId,
        ...data,
      })),
    };
  }, [getTimesheetsByTaskIds, getResourceHourlyRate, resources]);

  const value: ProductionManagementContextType = {
    resources,
    projects,
    users,
    timesheets,
    loadingResources,
    loadingProjects,
    loadingUsers,
    loadingTimesheets,
    errorResources,
    errorProjects,
    errorUsers,
    errorTimesheets,
    refreshResources,
    refreshProjects,
    refreshUsers,
    refreshTimesheets,
    refreshAll,
    getUserById,
    getUsersByIds,
    getUsersByEmails,
    getTimesheetsByContributorId,
    getTimesheetsByTaskIds,
    getResourceHourlyRate,
    calculateRealizedBudget,
  };

  return (
    <ProductionManagementContext.Provider value={value}>
      {children}
    </ProductionManagementContext.Provider>
  );
}

export function useProductionManagement() {
  const context = useContext(ProductionManagementContext);
  if (context === undefined) {
    throw new Error('useProductionManagement must be used within a ProductionManagementProvider');
  }
  return context;
}

export function useOptionalProductionManagement() {
  return useContext(ProductionManagementContext);
}
